import { canonicalize } from './canonical.js';
import { digestHex, verifyHex, isHex, sha256Hex } from './crypto.js';
import { utf8ToBytes } from '@noble/hashes/utils.js';
import { merkleRoot, merkleProof, type MerkleProof } from './merkle.js';
import { MAX_NOTES_PER_REQUEST, NOTE_DENOMS, noteAuthMessage, parseG2, verifyBlindSigs, verifyNotes } from './notes.js';
import { ENC_MAX_BYTES, ENC_MIN_BYTES } from './sealed.js';
import { requestShapeError } from './schema.js';
import {
  BPS,
  newPool,
  quoteBuy,
  applyBuy,
  quoteSell,
  applySell,
  maxBuyWithin,
  type Pool,
  type CurveParams,
  type FeeParams,
} from './curve.js';
import type { GenesisConfig, SignedRequest, SystemEvent, RequestBody } from './types.js';

/**
 * The deterministic state machine. The sequencer and every verifier run this
 * exact code over the same log and must end in the same state; any
 * divergence is proof that the log or the operator is lying.
 *
 * Contract: exec* either throws LedgerError without touching state, or
 * commits the whole effect and returns a canonical JSON result.
 */
export class LedgerError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
  }
}
const fail = (code: string, msg: string): never => {
  throw new LedgerError(code, msg);
};

export interface UserState {
  zat: bigint;
  nonce: number;
  depositIndex: number | null; // null: account opened by a note redemption, no deposit address
  address: string | null;
  tokens: Map<string, bigint>;
}

export interface TokenState {
  id: string;
  symbol: string;
  name: string;
  description: string;
  image: string;
  creator: string;
  createdSeq: number;
  createdTs: number;
  pool: Pool;
  held: bigint; // sum of all user balances of this token
  volumeZat: bigint;
  trades: number;
  graduatedSeq: number | null;
  /** End of the fair-launch window (ms). */
  launchEnd: number;
  /** ZEC committed to the launch batch per account, in arrival order; null once settled. */
  batch: Map<string, bigint> | null;
  batchZat: bigint;
  /** Tokens the launch batch bought (shown as "bought in the first minute"). */
  batchTokens: bigint;
}

export interface Withdrawal {
  id: number;
  user: string;
  zat: bigint; // debited from the user
  enc: string; // destination sealed to the sequencer (see sealed.ts)
  commit: string; // H(user ‖ salt ‖ address): the user can prove the destination, nobody else learns it
  sendZat: bigint; // amount sent on-chain (zat - withdrawFee)
  /** Payout transaction once it is on the network (withdraw_broadcast); until then nothing is in flight. */
  txid: string | null;
}

const SYMBOL_RE = /^[A-Z0-9]{1,10}$/;
/**
 * Characters refused in coin names and descriptions: control characters,
 * plus invisible and direction-changing ones that can make a name read as
 * something else on screen ("Official …" spelled backwards, hidden text).
 */
const isBadChar = (c: number) =>
  c <= 0x1f || (c >= 0x7f && c <= 0x9f) || c === 0xad || c === 0x61c || c === 0x180e || (c >= 0x200b && c <= 0x200f) ||
  (c >= 0x2028 && c <= 0x202e) || (c >= 0x2060 && c <= 0x206f) || c === 0xfeff || (c >= 0xfff9 && c <= 0xfffb);
const CTRL_RE = { test: (s: string) => [...s].some((ch) => isBadChar(ch.codePointAt(0)!)) };
/** Tickers nobody may launch: they pass for the currency itself or for the platform. */
const RESERVED_SYMBOLS = new Set(['ZEC', 'ZCASH', 'TAZ', 'DAZZLE', 'ZAT', 'ZATS', 'USD', 'USDT', 'USDC', 'BTC', 'ETH', 'SOL']);
const UINT_RE = /^(0|[1-9][0-9]{0,30})$/;
/** A request may be sequenced at most this long before the time it was signed (small clock differences). */
export const REQUEST_EARLY_MS = 2_000;
/** Why a withdrawal may be refunded (fixed words, never a wallet's error text). */
export const WITHDRAW_FAIL_REASONS = new Set(['unreadable_address', 'invalid_address', 'transparent_address', 'rejected_by_wallet', 'not_sent']);
/** Deposit reference: txid:pool:output index, in exactly one spelling. */
const DEPOSIT_REF_RE = /^([0-9a-f]{64}):(orchard|sapling|ironwood):(0|[1-9][0-9]{0,4})$/;

export function parseAmount(s: unknown, field: string): bigint {
  if (typeof s !== 'string' || !UINT_RE.test(s)) fail('bad_amount', `${field} must be a non-negative integer string`);
  return BigInt(s as string);
}

/**
 * Address formats per network. Not used by replay (the log only holds sealed
 * addresses); the bridge and the wallet use it before paying or sealing.
 */
export function isValidAddress(network: GenesisConfig['network'], address: string): boolean {
  return ADDRESS_RULES[network].test(address);
}

/**
 * Where withdrawals may go: shielded addresses only. The withdrawal txid is
 * public in the log, so a payment to a transparent address would show the
 * destination and amount on any block explorer, defeating the sealed address.
 */
export function isValidWithdrawalAddress(network: GenesisConfig['network'], address: string): boolean {
  return isValidAddress(network, address) && !/^(t1|t3|tm|t2)/.test(address);
}

const ADDRESS_RULES: Record<GenesisConfig['network'], RegExp> = {
  mainnet: /^(t1[1-9A-HJ-NP-Za-km-z]{33}|t3[1-9A-HJ-NP-Za-km-z]{33}|zs1[02-9ac-hj-np-z]{75}|u1[02-9ac-hj-np-z]{100,600})$/,
  testnet:
    /^(tm[1-9A-HJ-NP-Za-km-z]{33}|t2[1-9A-HJ-NP-Za-km-z]{33}|ztestsapling1[02-9ac-hj-np-z]{75}|utest1[02-9ac-hj-np-z]{100,600})$/,
  regtest: /^[0-9A-Za-z]{20,700}$/,
  mock: /^[0-9A-Za-z_-]{6,700}$/,
};

export class Ledger {
  readonly cfg: GenesisConfig;
  readonly curve: CurveParams;
  readonly fees: FeeParams;
  readonly createFee: bigint;
  readonly minTrade: bigint;
  readonly minWithdraw: bigint;
  readonly withdrawFee: bigint;
  /** Most tokens one account may hold while the wallet cap applies (null: no cap). */
  readonly walletCap: bigint | null;
  /** Identity of this log, which every user request must name (a request for one log is void on any other). */
  readonly chainId: string;

  users = new Map<string, UserState>();
  tokens = new Map<string, TokenState>();
  pending = new Map<number, Withdrawal>();
  depositRefs = new Set<string>();
  /** Running hash of credited deposit refs, so a checkpoint commits to exactly which deposits were credited. */
  depositAcc = '0'.repeat(64);
  /** Every txid recorded as a payout or expense: one transaction can never be counted twice. */
  usedTxids = new Set<string>();
  /** Deposit addresses in use (an address is never assigned twice). */
  private addresses = new Set<string>();
  nextDepositIndex = 0;
  totalDeposited = 0n;
  totalOutflow = 0n; // everything that left the vault on-chain (withdrawals + network fees + expenses)
  lastSeq = -1;
  /** Sequencing time of the last entry; entries may never go back in time (launch windows depend on it). */
  lastTs = 0;
  /** Shield notes: unredeemed count per denomination, spent serials, and a running hash of them for the state root. */
  notesOut = new Map<string, bigint>(NOTE_DENOMS.map((d) => [d, 0n]));
  spentSerials = new Set<string>();
  spentAcc = '0'.repeat(64);
  /** Every blinded point ever minted: a repeat would be a note that can never be redeemed twice, so it is refused before payment. */
  mintedBlinded = new Set<string>();
  mintedAcc = '0'.repeat(64);
  private noteKeys = new Map<string, NonNullable<ReturnType<typeof parseG2>>>();

  constructor(genesis: GenesisConfig) {
    validateGenesis(genesis);
    this.cfg = genesis;
    this.chainId = digestHex('chain', genesis);
    this.curve = {
      totalSupply: BigInt(genesis.totalSupply),
      curveSupply: BigInt(genesis.curveSupply),
      virtualZat: BigInt(genesis.virtualZat),
      virtualTokens: BigInt(genesis.virtualTokens),
    };
    newPool(this.curve); // validates curve params
    this.fees = { tradeBps: BigInt(genesis.tradeBps), creatorBps: BigInt(genesis.creatorBps) };
    this.createFee = BigInt(genesis.createFeeZat);
    this.minTrade = BigInt(genesis.minTradeZat);
    this.minWithdraw = BigInt(genesis.minWithdrawZat);
    this.withdrawFee = BigInt(genesis.withdrawFeeZat);
    this.walletCap = genesis.walletCapBps > 0 ? (this.curve.totalSupply * BigInt(genesis.walletCapBps)) / BPS : null;
    for (const d of NOTE_DENOMS) this.noteKeys.set(d, parseG2(genesis.noteKeys[d])!);
    // The fee account exists from the start so fees always have somewhere to go.
    this.users.set(genesis.feeAccount, this.blankUser(this.nextDepositIndex++));
    this.lastSeq = 0;
  }

  private blankUser(index: number | null): UserState {
    return { zat: 0n, nonce: 0, depositIndex: index, address: null, tokens: new Map() };
  }

  // ---------------------------------------------------------------- user ops

  /**
   * Checks signature, nonce and time window, then executes.
   * `blindSigs`: for note_mint, the sequencer's signatures on the blinded
   * points. The sequencer computes them with its key; replay takes them from
   * the stored result. Either way they are checked here by pairing.
   */
  execUser(sreq: SignedRequest, seq: number, ts: number, blindSigs?: unknown): Record<string, unknown> {
    this.expectSeq(seq);
    this.expectTs(ts);
    // Exact shape first: nothing malformed reaches code below, and nothing unknown gets into the log.
    const shape = requestShapeError(sreq);
    if (shape) fail('bad_request', shape);
    const { req, sig } = sreq;
    if (req.chain !== this.chainId) fail('wrong_chain', 'this request was signed for another Dazzle log');
    if (!isHex(req.user, 32)) fail('bad_request', 'user must be a 32-byte hex public key');
    if (req.nonce < 1) fail('bad_request', 'bad nonce');
    // Sequenced shortly after it was signed, never long before: honest users' clocks pin the sequencer's.
    if (ts - req.ts > this.cfg.requestWindowMs || req.ts - ts > REQUEST_EARLY_MS) fail('stale', 'request timestamp outside allowed window');
    let msg: string;
    try {
      msg = digestHex('request', req);
    } catch (e) {
      return fail('bad_request', `not canonical: ${(e as Error).message}`);
    }
    if (!verifyHex(sig, msg, req.user)) fail('bad_signature', 'signature does not match user key');

    const body = req.body as RequestBody;
    const existing = this.users.get(req.user);
    const expectedNonce = (existing?.nonce ?? 0) + 1;
    if (req.nonce !== expectedNonce) fail('bad_nonce', `expected nonce ${expectedNonce}`);

    let result: Record<string, unknown>;
    switch (body?.type) {
      case 'register':
        result = this.opRegister(req.user);
        break;
      case 'create':
        result = this.opCreate(req.user, body, seq, ts);
        break;
      case 'buy':
        result = this.opBuy(req.user, body.token, body.zat, body.minTokens, seq, ts);
        break;
      case 'sell':
        result = this.opSell(req.user, body.token, body.tokens, body.minZat, ts);
        break;
      case 'withdraw':
        result = this.opWithdraw(req.user, body.zat, body.enc, body.commit, seq);
        break;
      case 'note_mint':
        result = this.opNoteMint(req.user, body.denom, body.blinded, blindSigs);
        break;
      case 'note_redeem':
        result = this.opNoteRedeem(req.user, body.denom, body.notes);
        break;
      default:
        return fail('bad_request', 'unknown request type');
    }
    // Only reached on success: consume the nonce and advance.
    this.users.get(req.user)!.nonce = req.nonce;
    this.lastSeq = seq;
    this.lastTs = ts;
    return result;
  }

  // ------------------------------------------------------------- launch rules

  /** Coins whose launch window has passed but whose batch is not filled yet. The sequencer settles them first. */
  dueLaunches(ts: number): string[] {
    const due: string[] = [];
    for (const t of this.tokens.values()) if (t.batch !== null && ts >= t.launchEnd) due.push(t.id);
    return due;
  }

  /** Fees at time `ts`: normal, plus the falling anti-snipe fee (kept by the pool) right after the launch window. */
  feesAt(t: TokenState, ts: number): FeeParams {
    const base = this.fees.tradeBps + this.fees.creatorBps;
    const top = BigInt(this.cfg.snipeFeeBps);
    const window = BigInt(this.cfg.snipeWindowMs);
    const left = BigInt(t.launchEnd + this.cfg.snipeWindowMs - ts);
    if (window === 0n || top <= base || ts < t.launchEnd || left <= 0n) return this.fees;
    return { ...this.fees, poolBps: ((top - base) * left) / window };
  }

  /** Whether the wallet cap applies to this coin at `ts`. */
  private capApplies(t: TokenState, ts: number): boolean {
    return this.walletCap !== null && ts < t.createdTs + this.cfg.walletCapMs;
  }

  private checkCap(t: TokenState, ts: number, holding: bigint) {
    if (this.capApplies(t, ts) && holding > this.walletCap!)
      fail('wallet_cap', `no wallet may hold more than ${this.cfg.walletCapBps / 100}% of a coin during its first ${Math.round(this.cfg.walletCapMs / 60_000)} minutes`);
  }

  private requireUser(user: string): UserState {
    const u = this.users.get(user);
    if (!u) fail('no_account', 'register first');
    return u!;
  }

  private requireToken(id: unknown): TokenState {
    if (typeof id !== 'string') fail('bad_request', 'token must be a string');
    const t = this.tokens.get(id as string);
    if (!t) fail('no_token', 'unknown token');
    return t!;
  }

  private opRegister(user: string) {
    if (this.users.has(user)) {
      const u = this.users.get(user)!;
      if (u.nonce > 0 || user !== this.cfg.feeAccount) fail('exists', 'already registered');
      return { depositIndex: u.depositIndex }; // fee account claiming its pre-made account
    }
    const index = this.nextDepositIndex;
    this.users.set(user, this.blankUser(index));
    this.nextDepositIndex++;
    return { depositIndex: index };
  }

  private opCreate(user: string, body: Extract<RequestBody, { type: 'create' }>, seq: number, ts: number) {
    const u = this.requireUser(user);
    const symbol = String(body.symbol ?? '');
    const name = String(body.name ?? '');
    const description = String(body.description ?? '');
    const image = String(body.image ?? '');
    if (!SYMBOL_RE.test(symbol)) fail('bad_symbol', 'symbol: 1-10 chars A-Z 0-9');
    if (RESERVED_SYMBOLS.has(symbol)) fail('bad_symbol', 'this ticker is reserved');
    if (name.length < 1 || name.length > 32 || CTRL_RE.test(name)) fail('bad_name', 'name: 1-32 chars');
    if (description.length > 280 || CTRL_RE.test(description.replace(/\n/g, ' '))) fail('bad_description', 'description: up to 280 chars');
    if (image !== '' && (image.length > 300 || !/^https:\/\/[^\s"'<>]+$/.test(image))) fail('bad_image', 'image must be an https URL');
    const buyZat = parseAmount(body.buyZat, 'buyZat');
    const minTokens = parseAmount(body.minTokens, 'minTokens');
    if (buyZat > 0n && buyZat < this.minTrade) fail('too_small', 'dev buy below minimum trade');
    if (u.zat < this.createFee + buyZat) fail('insufficient', 'not enough ZEC for creation fee and dev buy');

    const id = 't' + seq;
    let pool = newPool(this.curve);
    let buyRes: Record<string, unknown> | null = null;
    let q = null;
    if (buyZat > 0n) {
      // The creator buys first, before the launch batch, but within the wallet cap like everyone.
      q = quoteBuy(pool, buyZat, this.fees);
      if (q.tokensOut < minTokens) fail('slippage', 'dev buy below minTokens');
      if (this.walletCap !== null && this.cfg.walletCapMs > 0 && q.tokensOut > this.walletCap)
        fail('wallet_cap', `the creator's first buy may take at most ${this.cfg.walletCapBps / 100}% of the coin`);
      pool = applyBuy(pool, q);
      buyRes = buyResult(q);
    }

    // commit
    const fee = this.users.get(this.cfg.feeAccount)!;
    u.zat -= this.createFee;
    fee.zat += this.createFee;
    const t: TokenState = {
      id,
      symbol,
      name,
      description,
      image,
      creator: user,
      createdSeq: seq,
      createdTs: ts,
      pool,
      held: 0n,
      volumeZat: 0n,
      trades: 0,
      graduatedSeq: null,
      launchEnd: ts + this.cfg.launchWindowMs,
      batch: this.cfg.launchWindowMs > 0 ? new Map() : null,
      batchZat: 0n,
      batchTokens: 0n,
    };
    this.tokens.set(id, t);
    if (q) this.settleBuy(u, t, q, seq);
    this.checkToken(t);
    this.checkSolvency();
    return { token: id, createFee: this.createFee.toString(), buy: buyRes };
  }

  private opBuy(user: string, tokenId: string, zatS: string, minTokensS: string, seq: number, ts: number) {
    const u = this.requireUser(user);
    const t = this.requireToken(tokenId);
    const zat = parseAmount(zatS, 'zat');
    const minTokens = parseAmount(minTokensS, 'minTokens');
    if (zat < this.minTrade) fail('too_small', 'below minimum trade');
    if (u.zat < zat) fail('insufficient', 'not enough ZEC');
    if (t.batch !== null) {
      if (ts >= t.launchEnd) fail('settling', 'the launch batch is being filled; try again in a moment');
      return this.joinBatch(user, u, t, zat);
    }
    let q;
    try {
      q = quoteBuy(t.pool, zat, this.feesAt(t, ts));
    } catch (e) {
      return fail('bad_trade', (e as Error).message);
    }
    if (q.tokensOut < minTokens) fail('slippage', 'price moved: tokens out below minimum');
    this.checkCap(t, ts, (u.tokens.get(t.id) ?? 0n) + q.tokensOut);
    const nextPool = applyBuy(t.pool, q);
    // commit
    t.pool = nextPool;
    this.settleBuy(u, t, q, seq);
    this.checkToken(t);
    this.checkSolvency();
    return buyResult(q);
  }

  /**
   * A buy during the launch window: the ZEC is set aside and filled with
   * everyone else's at one price when the window closes. The wallet cap is
   * enforced up front: an account may commit at most the largest amount that,
   * bought alone first, would get it no more than `cap − holding` tokens; a
   * shared fill is never cheaper per token, so its share stays within the cap.
   */
  private joinBatch(user: string, u: UserState, t: TokenState, zat: bigint) {
    const committed = (t.batch!.get(user) ?? 0n) + zat;
    if (this.walletCap !== null && this.cfg.walletCapMs > 0) {
      const room = this.walletCap - (u.tokens.get(t.id) ?? 0n);
      // null: the room is more than the whole rest of the curve, so no buy can pass the cap.
      const limit = room > 0n ? maxBuyWithin(t.pool, room, this.fees) : 0n;
      if (limit !== null && committed > limit)
        fail('wallet_cap', `in the launch minute one wallet may put in at most ${limit} zat (${this.cfg.walletCapBps / 100}% of the coin)`);
    }
    // commit
    u.zat -= zat;
    t.batch!.set(user, committed);
    t.batchZat += zat;
    this.checkSolvency();
    return { batched: true, zat: zat.toString(), committed: committed.toString(), fillsAt: t.launchEnd };
  }

  /**
   * Fills a launch batch: all committed ZEC buys from the curve in one go and
   * each account gets tokens (and any refund, if the curve sold out) in
   * proportion to what it put in. Rounding dust goes to the largest order.
   */
  private settleLaunch(t: TokenState, seq: number): Record<string, unknown> {
    const orders = [...t.batch!.entries()];
    const total = t.batchZat;
    if (total === 0n) {
      t.batch = null;
      return { token: t.id, orders: 0, zat: '0', tokens: '0', refund: '0' };
    }
    const q = quoteBuy(t.pool, total, this.fees);
    const nextPool = applyBuy(t.pool, q);
    const shares = splitBatch(orders, q.tokensOut, q.refund);
    // commit
    t.pool = nextPool;
    if (q.graduates && t.graduatedSeq === null) t.graduatedSeq = seq;
    for (const s of shares) {
      const u = this.users.get(s.user)!;
      if (s.tokens > 0n) u.tokens.set(t.id, (u.tokens.get(t.id) ?? 0n) + s.tokens);
      u.zat += s.refund;
    }
    this.users.get(this.cfg.feeAccount)!.zat += q.fee;
    this.users.get(t.creator)!.zat += q.creatorFee;
    t.held += q.tokensOut;
    t.volumeZat += q.zatUsed;
    t.trades += orders.length;
    t.batch = null;
    t.batchZat = 0n;
    t.batchTokens = q.tokensOut;
    this.checkToken(t);
    return {
      token: t.id,
      orders: orders.length,
      zat: q.zatUsed.toString(),
      tokens: q.tokensOut.toString(),
      refund: q.refund.toString(),
      fee: q.fee.toString(),
      creatorFee: q.creatorFee.toString(),
      graduated: q.graduates,
    };
  }

  private settleBuy(u: UserState, t: TokenState, q: ReturnType<typeof quoteBuy>, seq: number) {
    if (q.graduates && t.graduatedSeq === null) t.graduatedSeq = seq;
    u.zat -= q.zatUsed;
    this.users.get(this.cfg.feeAccount)!.zat += q.fee;
    this.users.get(t.creator)!.zat += q.creatorFee;
    u.tokens.set(t.id, (u.tokens.get(t.id) ?? 0n) + q.tokensOut);
    t.held += q.tokensOut;
    t.volumeZat += q.zatUsed;
    t.trades++;
  }

  private opSell(user: string, tokenId: string, tokensS: string, minZatS: string, ts: number) {
    const u = this.requireUser(user);
    const t = this.requireToken(tokenId);
    const tokens = parseAmount(tokensS, 'tokens');
    const minZat = parseAmount(minZatS, 'minZat');
    const bal = u.tokens.get(t.id) ?? 0n;
    if (tokens <= 0n) fail('too_small', 'amount must be positive');
    if (bal < tokens) fail('insufficient', 'not enough tokens');
    if (t.batch !== null) fail('launching', 'selling opens when the launch minute is over');
    let q;
    try {
      q = quoteSell(t.pool, tokens, this.feesAt(t, ts));
    } catch (e) {
      return fail('bad_trade', (e as Error).message);
    }
    // Selling everything is always allowed, so a small leftover never gets stuck.
    if (q.gross < this.minTrade && tokens !== bal) fail('too_small', 'below minimum trade');
    if (q.zatOut < minZat) fail('slippage', 'price moved: ZEC out below minimum');
    const nextPool = applySell(t.pool, q);
    // commit
    t.pool = nextPool;
    const left = bal - tokens;
    if (left === 0n) u.tokens.delete(t.id);
    else u.tokens.set(t.id, left);
    t.held -= tokens;
    u.zat += q.zatOut;
    this.users.get(this.cfg.feeAccount)!.zat += q.fee;
    this.users.get(t.creator)!.zat += q.creatorFee;
    t.volumeZat += q.gross;
    t.trades++;
    this.checkToken(t);
    this.checkSolvency();
    return {
      tokensIn: q.tokensIn.toString(),
      gross: q.gross.toString(),
      fee: q.fee.toString(),
      creatorFee: q.creatorFee.toString(),
      poolFee: q.poolFee.toString(),
      zatOut: q.zatOut.toString(),
    };
  }

  private opWithdraw(user: string, zatS: string, enc: unknown, commit: unknown, seq: number) {
    const u = this.requireUser(user);
    const zat = parseAmount(zatS, 'zat');
    // The address itself is sealed; a bad one is refunded by the bridge (withdraw_failed).
    if (!isHex(enc) || enc.length / 2 < ENC_MIN_BYTES || enc.length / 2 > ENC_MAX_BYTES) fail('bad_address', 'sealed address has a bad size');
    if (!isHex(commit, 32)) fail('bad_address', 'address commitment must be 32-byte hex');
    if (zat < this.minWithdraw) fail('too_small', 'below minimum withdrawal');
    if (zat <= this.withdrawFee) fail('too_small', 'amount does not cover the withdrawal fee');
    if (u.zat < zat) fail('insufficient', 'not enough ZEC');
    // commit
    u.zat -= zat;
    const w: Withdrawal = { id: seq, user, zat, enc: enc as string, commit: commit as string, sendZat: zat - this.withdrawFee, txid: null };
    this.pending.set(seq, w);
    this.checkSolvency();
    return { id: seq, sendZat: w.sendZat.toString(), fee: this.withdrawFee.toString() };
  }

  private noteKey(denom: unknown) {
    const pk = typeof denom === 'string' ? this.noteKeys.get(denom) : undefined;
    if (!pk) fail('bad_denom', `denom must be one of ${NOTE_DENOMS.join(', ')}`);
    return pk!;
  }

  private opNoteMint(user: string, denom: string, blinded: unknown, blindSigs: unknown) {
    const u = this.requireUser(user);
    const pk = this.noteKey(denom);
    if (!Array.isArray(blinded) || blinded.length < 1 || blinded.length > MAX_NOTES_PER_REQUEST) fail('bad_request', `1 to ${MAX_NOTES_PER_REQUEST} notes per request`);
    const pts = blinded as unknown[];
    if (pts.some((b) => !isHex(b, 48)) || new Set(pts).size !== pts.length) fail('bad_request', 'blinded points must be distinct 48-byte hex');
    // Same wallet counter used twice (two devices): refuse instead of minting a note that duplicates an existing one.
    if (pts.some((b) => this.mintedBlinded.has(b as string))) fail('dup_blinded', 'a blinded point was already minted; resync the wallet note counter');
    const cost = BigInt(denom) * BigInt(pts.length);
    if (u.zat < cost) fail('insufficient', 'not enough ZEC');
    const sigs = blindSigs as unknown[];
    if (!Array.isArray(sigs) || sigs.length !== pts.length || sigs.some((s) => !isHex(s, 48))) fail('bad_request', 'missing blind signatures');
    if (!verifyBlindSigs(pk, pts as string[], sigs as string[])) fail('bad_request', 'blinded points or signatures are not valid');
    // commit
    u.zat -= cost;
    this.notesOut.set(denom, this.notesOut.get(denom)! + BigInt(pts.length));
    for (const b of pts as string[]) {
      this.mintedBlinded.add(b);
      this.mintedAcc = sha256Hex(this.mintedAcc + b);
    }
    this.checkSolvency();
    return { denom, count: pts.length, sigs: sigs as string[] };
  }

  private opNoteRedeem(user: string, denom: string, notes: unknown) {
    const pk = this.noteKey(denom);
    if (!Array.isArray(notes) || notes.length < 1 || notes.length > MAX_NOTES_PER_REQUEST) fail('bad_request', `1 to ${MAX_NOTES_PER_REQUEST} notes per request`);
    const list = notes as { serial: unknown; sig: unknown; auth: unknown }[];
    if (list.some((n) => !n || typeof n !== 'object' || !isHex(n.serial, 32) || !isHex(n.sig, 48) || !isHex(n.auth, 64)))
      fail('bad_request', 'each note needs a 32-byte serial, a 48-byte signature and a 64-byte authorization');
    const serials = list.map((n) => n.serial as string);
    if (new Set(serials).size !== serials.length) fail('double_spend', 'the same note twice');
    if (serials.some((s) => this.spentSerials.has(`${denom}:${s}`))) fail('double_spend', 'note already redeemed');
    if (this.notesOut.get(denom)! < BigInt(list.length)) fail('bad_request', 'more notes than were ever minted');
    // Only the holder of the note's key can say which account it goes to: a note copied in flight is useless.
    if (list.some((n) => !verifyHex(n.auth as string, noteAuthMessage(this.chainId, denom, n.serial as string, user), n.serial as string)))
      fail('bad_signature', 'note authorization does not verify');
    if (!verifyNotes(pk, denom, list as { serial: string; sig: string }[])) fail('bad_signature', 'note signature does not verify');
    // commit: a new account is opened by its first redemption, without a deposit slot
    let u = this.users.get(user);
    if (!u) this.users.set(user, (u = this.blankUser(null)));
    u.zat += BigInt(denom) * BigInt(list.length);
    this.notesOut.set(denom, this.notesOut.get(denom)! - BigInt(list.length));
    for (const s of serials) {
      this.spentSerials.add(`${denom}:${s}`);
      this.spentAcc = sha256Hex(`${this.spentAcc}${denom}:${s}`);
    }
    this.checkSolvency();
    return { denom, count: list.length, balance: u.zat.toString() };
  }

  // -------------------------------------------------------------- system ops

  execSystem(ev: SystemEvent, seq: number, ts: number): Record<string, unknown> {
    this.expectSeq(seq);
    this.expectTs(ts);
    let result: Record<string, unknown>;
    switch (ev?.type) {
      case 'launch_settle': {
        const t = this.requireToken(ev.token);
        if (t.batch === null) fail('settled', 'launch batch already filled');
        if (ts < t.launchEnd) fail('too_early', 'launch window still open');
        result = this.settleLaunch(t, seq);
        break;
      }
      case 'assign_address': {
        const u = this.requireUser(ev.user);
        if (u.address !== null) fail('exists', 'address already assigned');
        if (ev.index !== u.depositIndex) fail('bad_index', 'index does not match account');
        if (typeof ev.address !== 'string' || ev.address.length < 6 || ev.address.length > 700) fail('bad_address', 'bad address');
        if (this.addresses.has(ev.address)) fail('dup_address', 'address reused');
        u.address = ev.address;
        this.addresses.add(ev.address);
        result = { user: ev.user, address: ev.address };
        break;
      }
      case 'deposit': {
        const u = this.requireUser(ev.user);
        const zat = parseAmount(ev.zat, 'zat');
        if (zat <= 0n) fail('bad_amount', 'deposit must be positive');
        if (!isHex(ev.txid, 32)) fail('bad_txid', 'txid must be 32-byte hex');
        // One spelling per output, naming this very transaction: the same output can never be credited twice.
        const m = typeof ev.ref === 'string' ? DEPOSIT_REF_RE.exec(ev.ref) : null;
        if (!m || m[1] !== ev.txid) fail('bad_ref', 'deposit ref must be txid:pool:index of this transaction');
        if (this.depositRefs.has(ev.ref)) fail('dup_deposit', 'deposit already credited');
        if (u.address === null) fail('no_address', 'user has no deposit address');
        u.zat += zat;
        this.depositRefs.add(ev.ref);
        this.depositAcc = sha256Hex(this.depositAcc + ev.ref);
        this.totalDeposited += zat;
        result = { user: ev.user, zat: zat.toString(), balance: u.zat.toString() };
        break;
      }
      case 'withdraw_broadcast': {
        const w = this.pending.get(ev.id);
        if (!w) fail('no_withdrawal', 'unknown or already settled withdrawal');
        if (!isHex(ev.txid, 32)) fail('bad_txid', 'txid must be 32-byte hex');
        if (this.usedTxids.has(ev.txid)) fail('dup_txid', 'transaction already used');
        // A rebuilt transaction (the first expired) replaces the old txid.
        w!.txid = ev.txid;
        result = { id: ev.id, txid: ev.txid };
        break;
      }
      case 'withdraw_sent': {
        const w = this.pending.get(ev.id);
        if (!w) fail('no_withdrawal', 'unknown or already settled withdrawal');
        if (!isHex(ev.txid, 32)) fail('bad_txid', 'txid must be 32-byte hex');
        if (this.usedTxids.has(ev.txid)) fail('dup_txid', 'transaction already counted as another payout');
        if (w!.txid !== null && w!.txid !== ev.txid) fail('bad_txid', 'not the transaction that was broadcast');
        const netFee = parseAmount(ev.networkFeeZat, 'networkFeeZat');
        // The user paid withdrawFee; the difference to the real network fee goes to/comes from the fee account.
        const diff = this.withdrawFee - netFee;
        const feeAcc = this.users.get(this.cfg.feeAccount)!;
        if (feeAcc.zat + diff < 0n) fail('fee_account_empty', 'fee account cannot cover network fee');
        this.pending.delete(ev.id);
        this.usedTxids.add(ev.txid);
        feeAcc.zat += diff;
        this.totalOutflow += w!.sendZat + netFee;
        result = { id: ev.id, txid: ev.txid, sent: w!.sendZat.toString(), networkFee: netFee.toString() };
        break;
      }
      case 'withdraw_failed': {
        const w = this.pending.get(ev.id);
        if (!w) fail('no_withdrawal', 'unknown or already settled withdrawal');
        // Fixed reasons only: a wallet's raw error text could quote the sealed address into the public log.
        if (!WITHDRAW_FAIL_REASONS.has(ev.reason)) fail('bad_reason', `reason must be one of ${[...WITHDRAW_FAIL_REASONS].join(', ')}`);
        // Once a payout is on the network it may still be mined: refunding it too would pay twice.
        if (w!.txid !== null) fail('broadcast', 'this withdrawal was broadcast; it can only be settled by its transaction');
        this.pending.delete(ev.id);
        this.users.get(w!.user)!.zat += w!.zat; // full refund
        result = { id: ev.id, refunded: w!.zat.toString() };
        break;
      }
      case 'expense': {
        const zat = parseAmount(ev.zat, 'zat');
        if (!isHex(ev.txid, 32)) fail('bad_txid', 'txid must be 32-byte hex');
        if (this.usedTxids.has(ev.txid)) fail('dup_txid', 'transaction already counted');
        if (typeof ev.note !== 'string' || ev.note.length > 200) fail('bad_note', 'note up to 200 chars');
        const feeAcc = this.users.get(this.cfg.feeAccount)!;
        if (feeAcc.zat < zat) fail('fee_account_empty', 'fee account cannot cover expense');
        feeAcc.zat -= zat;
        this.usedTxids.add(ev.txid);
        this.totalOutflow += zat;
        result = { zat: zat.toString(), txid: ev.txid };
        break;
      }
      default:
        return fail('bad_event', 'unknown system event');
    }
    this.checkSolvency();
    this.lastSeq = seq;
    this.lastTs = ts;
    return result;
  }

  private expectSeq(seq: number) {
    if (seq !== this.lastSeq + 1) fail('bad_seq', `expected seq ${this.lastSeq + 1}, got ${seq}`);
  }

  private expectTs(ts: number) {
    if (!Number.isSafeInteger(ts) || ts < this.lastTs) fail('bad_ts', 'entry time goes backwards');
  }

  // ------------------------------------------------------------- invariants

  /** ZEC owed to users + held by pools and launch batches + pending withdrawals + unredeemed notes must equal what is in the vault. */
  liabilities(): bigint {
    let sum = this.notesZat();
    for (const u of this.users.values()) sum += u.zat;
    for (const t of this.tokens.values()) sum += t.pool.realZat + t.batchZat;
    for (const w of this.pending.values()) sum += w.zat;
    return sum;
  }

  /** ZEC held as unredeemed shield notes. */
  notesZat(): bigint {
    let sum = 0n;
    for (const [d, n] of this.notesOut) sum += BigInt(d) * n;
    return sum;
  }

  expectedVault(): bigint {
    return this.totalDeposited - this.totalOutflow;
  }

  private checkSolvency() {
    // O(users) per op is fine for a beta; switch to running totals if it shows up in profiles.
    if (this.liabilities() !== this.expectedVault()) throw new Error('INVARIANT BROKEN: ZEC not conserved');
    for (const u of this.users.values()) if (u.zat < 0n) throw new Error('INVARIANT BROKEN: negative balance');
  }

  private checkToken(t: TokenState) {
    if (t.held + t.pool.realTok + t.pool.reserveTok !== this.curve.totalSupply) throw new Error('INVARIANT BROKEN: token supply');
  }

  // ------------------------------------------------------------- state root

  /** Every piece of state as canonical leaf records, in a fixed order. */
  stateRecords(): unknown[] {
    const recs: unknown[] = [];
    recs.push({
      k: 'g',
      seq: this.lastSeq,
      ts: this.lastTs,
      deposited: this.totalDeposited.toString(),
      outflow: this.totalOutflow.toString(),
      nextDepositIndex: this.nextDepositIndex,
      deposits: this.depositRefs.size,
      notes: NOTE_DENOMS.map((d) => [d, this.notesOut.get(d)!.toString()]),
      spent: this.spentSerials.size,
      spentAcc: this.spentAcc,
      minted: this.mintedBlinded.size,
      mintedAcc: this.mintedAcc,
      depositAcc: this.depositAcc,
      usedTxids: [...this.usedTxids].sort(),
    });
    for (const id of [...this.users.keys()].sort()) recs.push(this.userRecord(id));
    for (const id of [...this.tokens.keys()].sort()) {
      const t = this.tokens.get(id)!;
      recs.push({
        k: 't',
        id,
        symbol: t.symbol,
        name: t.name,
        description: t.description,
        image: t.image,
        creator: t.creator,
        createdSeq: t.createdSeq,
        mode: t.pool.mode,
        vZat: t.pool.vZat.toString(),
        vTok: t.pool.vTok.toString(),
        realZat: t.pool.realZat.toString(),
        realTok: t.pool.realTok.toString(),
        reserveTok: t.pool.reserveTok.toString(),
        held: t.held.toString(),
        createdTs: t.createdTs,
        launchEnd: t.launchEnd,
        batchOpen: t.batch !== null,
        batchZat: t.batchZat.toString(),
        batchTokens: t.batchTokens.toString(),
      });
      // Launch orders keep their arrival order: it decides who gets the rounding dust.
      if (t.batch) [...t.batch.entries()].forEach(([user, zat], i) => recs.push({ k: 'b', token: id, i, user, zat: zat.toString() }));
    }
    for (const id of [...this.pending.keys()].sort((a, b) => a - b)) {
      const w = this.pending.get(id)!;
      recs.push({ k: 'w', id, user: w.user, zat: w.zat.toString(), commit: w.commit, enc: sha256Hex(w.enc), txid: w.txid });
    }
    return recs;
  }

  userRecord(id: string) {
    const u = this.users.get(id);
    if (!u) return null;
    return {
      k: 'u',
      id,
      zat: u.zat.toString(),
      nonce: u.nonce,
      depositIndex: u.depositIndex,
      address: u.address,
      tokens: [...u.tokens.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([tid, v]) => [tid, v.toString()]),
    };
  }

  /** Leaves of the current state, kept until the next entry changes it (every change advances lastSeq). */
  private leafCache: { seq: number; recs: unknown[]; bytes: Uint8Array[]; root: string } | null = null;

  private leaves(): { recs: unknown[]; bytes: Uint8Array[]; root: string } {
    if (this.leafCache?.seq === this.lastSeq) return this.leafCache;
    const recs = this.stateRecords();
    const bytes = recs.map((r) => utf8ToBytes(canonicalize(r)));
    this.leafCache = { seq: this.lastSeq, recs, bytes, root: merkleRoot(bytes) };
    return this.leafCache;
  }

  stateRoot(): string {
    return this.leaves().root;
  }

  /** Inclusion proof for one account, checkable against a signed checkpoint. */
  accountProof(user: string): { record: unknown; proof: MerkleProof; root: string } | null {
    const { recs, bytes, root } = this.leaves();
    const idx = recs.findIndex((r) => (r as { k: string; id?: string }).k === 'u' && (r as { id: string }).id === user);
    if (idx < 0) return null;
    return { record: recs[idx], proof: merkleProof(bytes, idx), root };
  }
}

/**
 * How a filled launch batch is shared: tokens and refund in proportion to
 * each order, rounding dust to the largest order (the first one on a tie).
 */
export function splitBatch(orders: [string, bigint][], tokensOut: bigint, refund: bigint) {
  const total = orders.reduce((s, [, z]) => s + z, 0n);
  let largest = 0;
  orders.forEach(([, z], i) => {
    if (z > orders[largest][1]) largest = i;
  });
  const shares = orders.map(([user, zat]) => ({ user, zat, tokens: (tokensOut * zat) / total, refund: (refund * zat) / total }));
  shares[largest].tokens += tokensOut - shares.reduce((s, x) => s + x.tokens, 0n);
  shares[largest].refund += refund - shares.reduce((s, x) => s + x.refund, 0n);
  return shares;
}

function buyResult(q: ReturnType<typeof quoteBuy>) {
  return {
    zatUsed: q.zatUsed.toString(),
    refund: q.refund.toString(),
    fee: q.fee.toString(),
    creatorFee: q.creatorFee.toString(),
    poolFee: q.poolFee.toString(),
    tokensOut: q.tokensOut.toString(),
    graduated: q.graduates,
  };
}

export function validateGenesis(g: GenesisConfig) {
  if (g?.protocol !== 'dazzle/3') throw new Error('genesis: bad protocol');
  if (!['mainnet', 'testnet', 'regtest', 'mock'].includes(g.network)) throw new Error('genesis: bad network');
  if (!isHex(g.sequencer, 32) || !isHex(g.feeAccount, 32)) throw new Error('genesis: bad keys');
  for (const k of ['totalSupply', 'curveSupply', 'virtualZat', 'virtualTokens', 'createFeeZat', 'minTradeZat', 'minWithdrawZat', 'withdrawFeeZat'] as const) {
    if (typeof g[k] !== 'string' || !UINT_RE.test(g[k])) throw new Error(`genesis: bad ${k}`);
  }
  for (const k of [
    'tokenDecimals',
    'tradeBps',
    'creatorBps',
    'requestWindowMs',
    'createdAt',
    'launchWindowMs',
    'snipeWindowMs',
    'snipeFeeBps',
    'walletCapBps',
    'walletCapMs',
  ] as const) {
    if (!Number.isSafeInteger(g[k]) || g[k] < 0) throw new Error(`genesis: bad ${k}`);
  }
  if (g.tradeBps + g.creatorBps >= 10_000) throw new Error('genesis: fees too high');
  if (g.snipeFeeBps >= 10_000) throw new Error('genesis: anti-snipe fee too high');
  if (g.walletCapBps > 10_000) throw new Error('genesis: bad walletCapBps');
  if (!isHex(g.encKey, 32)) throw new Error('genesis: bad encKey');
  if (!g.noteKeys || typeof g.noteKeys !== 'object' || Object.keys(g.noteKeys).sort().join() !== [...NOTE_DENOMS].sort().join())
    throw new Error('genesis: noteKeys must cover exactly the note denominations');
  for (const d of NOTE_DENOMS) if (!parseG2(g.noteKeys[d])) throw new Error(`genesis: bad note key for ${d}`);
}
